import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  AppDatePicker,
  AppInput,
  AppModal,
  AppMultiSelect,
  AppRemixIcon,
  AppSingleSelect,
  AppInputNumber,
} from "@/layout/AppComponents";
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import { axiosPath } from "../../../../routing/helpers/router";
import errorHandler from "../../../components/error/ErrorHandler";
import useIsMounted from "../../../components/hooks/useIsMounted";
import AppLink from "../../../../routing/helpers/AppLink";

export default function EnvironmentalAspectsAssessmentTopButtons({
  activeRoute,
  ah,
}) {
  const { moduleBridgeHex, plantHex, assessmentHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const assessment = useSelector((s) => s?.currentPage?.assessment);
  const hiddenNoApplyAspects = useSelector(
    (state) => state.currentPage?.hiddenNoApplyAspects,
  );
  const showOnlyUnAssessed = useSelector(
    (state) => state.currentPage?.showOnlyUnAssessed,
  );
  const showOnlySignificant = useSelector(
    (state) => state.currentPage?.showOnlySignificant,
  );
  const periodicityOptions = useSelector(
    (state) => state.currentPage?.periodicityOptions ?? [],
  );
  const [showAddAssessment, setShowAddAssessment] = useState();
  const [showAddInspection, setShowAddInspection] = useState();
  const [tipologyOptions, setTipologyOptions] = useState();
  const [assessmentOptions, setAssessmentOptions] = useState();
  const [aspectOptions, setAspectOptions] = useState();
  const [checkAspectInspectionsModal, setCheckAspectInspectionsModal] = useState();
  const [assessmentValidationSchema] = useState(
    Yup.object().shape({
      title: Yup.string().required("Requerido"),
    }),
  );
  const [inspectionValidationSchema] = useState(
    Yup.object().shape({
      description: Yup.string().required("Requerido"),
      code: Yup.number().required("Requerido"),
      periodicity: Yup.number().nullable().required("Requerido"),
      linkedAspects: Yup.array()
        .of(Yup.number().required("Requerido"))
        .min(1, "Seleccione al menos un aspecto"),
    }),
  );

  // > LOAD TIPOLOGIES
  const loadTipologies = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_environmentalAspectsAssessment_tipology_list"),
          {
            moduleBridgeHex,
            plantHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setTipologyOptions(data);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex],
  );

  useEffect(() => {
    if (!assessmentHex) loadTipologies();
  }, []);
  // < LOAD TIPOLOGIES

  // > LOAD CLONE EVALUATIONS
  const loadCloneEvaluations = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_environmentalAspectsAssessment_assessment_clone",
          ),
          {
            moduleBridgeHex,
            plantHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setAssessmentOptions(data);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, plantHex],
  );

  useEffect(() => {
    if (!assessmentHex) loadCloneEvaluations();
  }, []);
  // < LOAD CLONE EVALUATIONS

  // > LOAD ASPECTS OPTIONS
  const loadAspects = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_environmentalAspectsAssessment_inspection_aspect_list",
          ),
          {
            moduleBridgeHex,
            assessmentHex,
            plantHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setAspectOptions(data);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, plantHex, assessmentHex],
  );

  useEffect(() => {
    if (
      activeRoute === "user_module_environmentalAspectsAssessment_inspection_home"
    ) {
      loadAspects();
    }
  }, []);
  // < LOAD ASPECTS OPTIONS

  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_environmentalAspectsAssessment_assessment_add",
          ),
          {
            moduleBridgeHex,
            plantHex,
            ...payload,
          },
        )
        .then(() => window.location.reload())
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, plantHex],
  );

  const handleAddInspection = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_environmentalAspectsAssessment_inspection_add",
          ),
          {
            moduleBridgeHex,
            assessmentHex,
            plantHex,
            ...payload,
          },
        )
        .then(() => window.location.reload())
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, assessmentHex, plantHex],
  );

  const checkAspectInspections = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_environmentalAspectsAssessment_assessment_inspection_check",
          ),
          {
            moduleBridgeHex,
            assessmentHex,
            plantHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) setCheckAspectInspectionsModal(data);
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, assessmentHex, plantHex],
  );

  return (
    <div className="mb-3">
      {[
        "user_module_environmentalAspectsAssessment_assessment_current",
        "user_module_environmentalAspectsAssessment_assessment_home",
      ].includes(activeRoute) && (
        <>
          {!assessmentHex && (
            <button
              type="button"
              className="btn btn-xs btn-success d-inline-flex mr-3"
              onClick={() => setShowAddAssessment(true)}
            >
              <AppRemixIcon icon="add" className="mr-1" /> Nueva evaluación
            </button>
          )}
          {assessment?.closed && (
            <>
              <AppLink
                path="user_module_environmentalAspectsAssessment_inspection_home"
                params={{
                  moduleBridgeHex,
                  plantHex,
                  assessmentHex: ah,
                }}
                className="btn btn-xs btn-primary d-inline-flex mr-3"
              >
                Ir a Inspección
              </AppLink>
              <button
                type="button"
                className="btn btn-xs btn-warning d-inline-flex mr-3"
                onClick={checkAspectInspections}
              >
                Comprobar inspecciones
              </button>
              <AppModal
                show={!!checkAspectInspectionsModal}
                title="Comprobación de inspecciones vinculadas a aspectos"
                onClose={() => setCheckAspectInspectionsModal(undefined)}
              >
                {checkAspectInspectionsModal?.length > 0 ? (
                  <>
                    <p>
                      Los siguientes aspectos no tienen vinculada ninguna inspección
                      en esta evaluación:
                    </p>
                    <ul>
                      {checkAspectInspectionsModal?.map((ai) => (
                        <li key={ai?.hex}>{ai?.aspect}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p>
                    Todos los aspectos tienen al menos una inspección vinculada en
                    esta evaluación
                  </p>
                )}
              </AppModal>
            </>
          )}
          <button
            type="button"
            className={`btn btn-xs btn${
              hiddenNoApplyAspects ? "-outline" : ""
            }-info d-inline-flex mr-3`}
            onClick={() =>
              dispatch(
                currentPageAction.change(
                  "hiddenNoApplyAspects",
                  !hiddenNoApplyAspects,
                  true,
                ),
              )
            }
          >
            <AppRemixIcon
              icon={hiddenNoApplyAspects ? "eye" : "eye-off"}
              className="mr-1"
            />
            {hiddenNoApplyAspects ? "Mostrar todos" : "Mostrar sólo aplicables"}
          </button>
          <button
            type="button"
            className={`btn btn-xs btn${
              showOnlyUnAssessed ? "-outline" : ""
            }-danger d-inline-flex mr-3`}
            onClick={() =>
              dispatch(
                currentPageAction.change(
                  "showOnlyUnAssessed",
                  !showOnlyUnAssessed,
                  true,
                ),
              )
            }
          >
            <AppRemixIcon
              icon={showOnlyUnAssessed ? "eye" : "eye-off"}
              className="mr-1"
            />
            {showOnlyUnAssessed ? "Mostrar todos" : "Mostrar sólo no evaluados"}
          </button>
          <button
            type="button"
            className={`btn btn-xs btn${
              showOnlySignificant ? "-outline" : ""
            }-light d-inline-flex mr-3`}
            onClick={() =>
              dispatch(
                currentPageAction.change(
                  "showOnlySignificant",
                  !showOnlySignificant,
                  true,
                ),
              )
            }
          >
            <AppRemixIcon
              icon={showOnlySignificant ? "eye" : "eye-off"}
              className="mr-1"
            />
            {showOnlySignificant ? "Mostrar todos" : "Mostrar sólo significativos"}
          </button>
          <AppModal
            show={showAddAssessment}
            title="Añadir evaluación"
            onClose={() => setShowAddAssessment(false)}
          >
            <Formik
              initialValues={{
                title: "",
                tipology: null,
                assessmentHex: null,
                code: "",
                date: null,
              }}
              validationSchema={assessmentValidationSchema}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={handleSave}
            >
              {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <AppInput
                    title="Título"
                    value={values.title}
                    onChange={(v) => setFieldValue("title", v)}
                    error={errors.title}
                    isClearable
                    required
                  />
                  <AppInputNumber
                    title="Código"
                    value={values.code}
                    error={errors.code}
                    onChange={(v) => setFieldValue("code", v)}
                    isClearable
                  />
                  <AppDatePicker
                    title="Fecha"
                    locale="es"
                    value={values?.date}
                    error={errors?.date}
                    placeholder="DD/MM/YYYY"
                    onChange={(opt) => setFieldValue("date", opt)}
                    popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                    isClearable
                    showYearDropdown
                    showMonthDropdown
                    hasToggleType
                  />
                  <AppSingleSelect
                    placeholder="Selecciona una tipología"
                    options={tipologyOptions}
                    title="Tipología"
                    value={values.tipology}
                    onChange={(v) => {
                      setFieldValue("assessmentHex", null);
                      setFieldValue("tipology", v);
                    }}
                    isLoading={!tipologyOptions}
                    isClearable
                  />
                  <AppSingleSelect
                    placeholder="Selecciona una evaluación a clonar"
                    options={assessmentOptions}
                    title="Clonar evaluación"
                    value={values.assessmentHex}
                    error={errors.assessmentHex}
                    onChange={(v) => {
                      setFieldValue("tipology", null);
                      setFieldValue("assessmentHex", v);
                    }}
                    isLoading={!assessmentOptions}
                    isClearable
                  />
                  <button
                    type="submit"
                    className="btn btn-success btn-sm mt-2 float-right"
                    disabled={isSubmitting}
                  >
                    Añadir
                  </button>
                </form>
              )}
            </Formik>
          </AppModal>
        </>
      )}
      {activeRoute ===
        "user_module_environmentalAspectsAssessment_inspection_home" && (
        <>
          <button
            type="button"
            className="btn btn-xs btn-success d-inline-flex mr-3"
            onClick={() => setShowAddInspection(true)}
          >
            <AppRemixIcon icon="add" className="mr-1" /> Nueva inspección
          </button>
          <AppModal
            show={showAddInspection}
            title="Añadir inspección"
            onClose={() => setShowAddInspection(false)}
          >
            <Formik
              initialValues={{
                description: "",
                code: "",
                periodicity: null,
                linkedAspects: [],
                acceptanceCriteria: "",
              }}
              validationSchema={inspectionValidationSchema}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={handleAddInspection}
            >
              {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <AppInput
                    title="Inspección"
                    value={values.description}
                    onChange={(v) => setFieldValue("description", v)}
                    error={errors.description}
                    isClearable
                    required
                  />
                  <AppInput
                    title="Código"
                    value={values.code}
                    error={errors.code}
                    onChange={(v) => setFieldValue("code", v)}
                    isClearable
                    required
                  />
                  <AppSingleSelect
                    options={periodicityOptions}
                    title="Periodicidad"
                    value={values?.periodicity}
                    error={errors.periodicity}
                    onChange={(opt) => setFieldValue("periodicity", opt)}
                    isClearable
                    hasSearchBox
                    required
                  />
                  <AppInput
                    title="Criterio de aceptación"
                    value={values.acceptanceCriteria}
                    onChange={(v) => setFieldValue("acceptanceCriteria", v)}
                    error={errors.acceptanceCriteria}
                    isClearable
                  />
                  <AppMultiSelect
                    placeholder="Selecciona aspecto/s"
                    options={aspectOptions}
                    title="Aspecto"
                    value={values?.linkedAspects}
                    error={errors.linkedAspects}
                    onChange={(opt) => setFieldValue("linkedAspects", opt)}
                    isLoading={!aspectOptions}
                    isClearable
                    hasSearchBox
                    required
                  />
                  <button
                    type="submit"
                    className="btn btn-success btn-sm mt-2 float-right"
                    disabled={isSubmitting}
                  >
                    Añadir
                  </button>
                </form>
              )}
            </Formik>
          </AppModal>
        </>
      )}
    </div>
  );
}
